import { getMinutes } from "./getMinutes";

interface Report {
  id: number;
  start_time: string;
  end_time: string;
  date_conference?: string;
  continuation_date?: string;
  continuation_start_time?: string;
  isOvernightContinuation?: boolean;
  // другие поля...
  thereIsCrossroadAtOneHourOnOneDay?: boolean; // добавляемое поле
}

interface DateGroup {
  date: string;
  reports: Report[];
}

export function addOvernightContinuations(dateGroups: DateGroup[]): DateGroup[] {
  const groupsByDate = new Map(dateGroups.map(group => [group.date, group]));

  return dateGroups.map(group => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(group.date ?? ''))) return group;
    const previousDate = new Date(`${group.date}T00:00:00Z`);
    previousDate.setUTCDate(previousDate.getUTCDate() - 1);
    const previousGroup = groupsByDate.get(previousDate.toISOString().slice(0, 10));
    const continuations = previousGroup?.reports
      .filter(report => getMinutes(report.end_time) < getMinutes(report.start_time))
      .map(report => ({
        ...report,
        date_conference: group.date,
        start_time: '00:00:00',
        continuation_date: previousGroup.date,
        continuation_start_time: report.start_time,
        isOvernightContinuation: true,
      })) ?? [];

    return continuations.length ? {...group, reports: [...group.reports, ...continuations]} : group;
  });
}

export function addBoundaryOvernightReports(dateGroups: DateGroup[], requestedStart: string): DateGroup[] {
  if (!Array.isArray(dateGroups) || !requestedStart) return dateGroups;

  const previousDateObject = new Date(`${requestedStart}T00:00:00Z`);
  previousDateObject.setUTCDate(previousDateObject.getUTCDate() - 1);
  const previousDate = previousDateObject.toISOString().slice(0, 10);
  const previousGroup = dateGroups.find(group => group.date === previousDate);
  const visibleGroups = dateGroups.filter(group => group.date >= requestedStart);
  const firstGroup = visibleGroups.find(group => group.date === requestedStart);
  if (!previousGroup || !firstGroup) return visibleGroups;

  const continuations = (previousGroup.reports ?? [])
    .filter(report => getMinutes(report.end_time) < getMinutes(report.start_time))
    .map(report => ({
      ...report,
      date_conference: requestedStart,
      start_time: '00:00:00',
      continuation_date: previousDate,
      continuation_start_time: report.start_time,
      isOvernightContinuation: true,
    }));

  return continuations.length
    ? visibleGroups.map(group => group === firstGroup
      ? {...group, reports: [...group.reports, ...continuations]}
      : group)
    : visibleGroups;
}

/**
 * Проверяет, пересекаются ли два временных интервала в пределах одного часа
 * @param start1 - время начала первого интервала в минутах
 * @param end1 - время окончания первого интервала в минутах
 * @param start2 - время начала второго интервала в минутах
 * @param end2 - время окончания второго интервала в минутах
 * @returns true если интервалы пересекаются в пределах одного часа
 */
function hasHourOverlap(
  start1: number,
  end1: number,
  start2: number,
  end2: number
): boolean {
  // Проверяем пересечение интервалов
  const hasOverlap = Math.max(start1, start2) < Math.min(end1, end2);
  
  if (!hasOverlap) return false;
  
  // Проверяем, что оба начинаются в один час (например, 8:00-8:59)
  const start1Hour = Math.floor(start1 / 60);
  const start2Hour = Math.floor(start2 / 60);
  
  return start1Hour === start2Hour;
}

/**
 * Добавляет поле thereIsCrossroadAtOneHourOnOneDay к каждому отчету,
 * указывающее наличие пересечения по времени с другим занятием в тот же день
 * в пределах одного часа начала(если занятия начинаются в один час)
 * @param dateGroups - массив объектов с датами и отчетами
 * @returns массив с добавленными полями пересечений
 */
export function addThereIsCrossroadAtOneHourOnOneDay(dateGroups: DateGroup[]): DateGroup[] {
  return dateGroups.map(dateGroup => {
    const reports = dateGroup.reports;
    const reportsWithCrossroad = reports.map(report => {
      // Преобразуем время в минуты
      const startMinutes = getMinutes(report.start_time);
      const rawEndMinutes = getMinutes(report.end_time);
      const endMinutes = rawEndMinutes < startMinutes ? rawEndMinutes + 24 * 60 : rawEndMinutes;
      
      // Проверяем пересечения со всеми другими отчетами этого дня
      const hasCrossroad = reports.some(otherReport => {
        // Не сравниваем с самим собой
        if (otherReport.id === report.id) return false;
        
        const otherStart = getMinutes(otherReport.start_time);
        const otherRawEnd = getMinutes(otherReport.end_time);
        const otherEnd = otherRawEnd < otherStart ? otherRawEnd + 24 * 60 : otherRawEnd;
        
        return hasHourOverlap(startMinutes, endMinutes, otherStart, otherEnd);
      });
      
      // Создаем копию отчета с добавленным полем
      return {
        ...report,
        thereIsCrossroadAtOneHourOnOneDay: hasCrossroad
      };
    });
    
    return {
      ...dateGroup,
      reports: reportsWithCrossroad
    };
  });
}
